import React from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import { Mail, Phone, Instagram, Linkedin, Heart, Lightbulb, Target, Users } from "lucide-react";
import { Link } from "react-router-dom";

const About = () => {
  const values = [
    {
      icon: Target,
      title: "Pragmatisme",
      description: "Des actions applicables, pas des concepts",
    },
    {
      icon: Lightbulb,
      title: "Clarté",
      description: "Des chiffres simples, des décisions rapides",
    },
    {
      icon: Users,
      title: "Respect du métier",
      description: "Tradition + méthode",
    },
    {
      icon: Heart,
      title: "Bienveillance",
      description: "On parle vrai, sans juger",
    },
  ];

  // Schema.org Person structured data
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Christophe Marrama",
    "jobTitle": "Consultant rentabilité boulangerie/pâtisserie",
    "email": "christophe.marrama@croissanceconsulting.net",
    "telephone": "+33674245563",
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "name": "Annecy"
      },
      "description": "Annecy, Savoie, Haute-Savoie"
    }
  };

  return (
    <>
      <Helmet>
        <title>À propos | Consultant boulangerie pâtisserie Annecy | Croissance Consulting</title>
        <meta name="description" content="Découvrez le parcours de Christophe Marrama, consultant terrain en boulangerie-pâtisserie. Expérience artisanale, gestion, marge, organisation et accompagnement concret." />
        <link rel="canonical" href="https://croissanceconsulting.net/a-propos" />
        <script type="application/ld+json">
          {JSON.stringify(personSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#FBF7EF]">
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
              À propos – Christophe Marrama, consultant boulangerie/pâtisserie
            </h1>

          {/* Kitchen Photo - moved up */}
          <div className="mb-12">
            <div className="rounded-2xl overflow-hidden shadow-xl relative">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#FBF7EF]/30 z-10"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_diagnostic-fournil/artifacts/3t0bunb2_ChatGPT%20Image%2027%20avr.%202026%2C%2016_30_41.png"
                alt="Christophe Marrama dans sa cuisine professionnelle"
                className="w-full h-[600px] object-cover"
                style={{ 
                  objectPosition: "center center",
                  filter: "brightness(1.05) contrast(1.05)"
                }}
              />
            </div>
          </div>

          {/* Bio */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-lg mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#4A3A2A] font-poppins mb-6">
              Avant d'accompagner les artisans, j'ai été artisan moi-même.
            </h2>
            
            <p className="text-lg text-[#2D2D2D] leading-relaxed mb-4">
              J'ai découvert ce métier à 15 ans, par la voie classique de l'apprentissage.
            </p>
            
            <p className="text-lg text-[#2D2D2D] leading-relaxed mb-4">
              Je suis titulaire d'un CAP et d'un BTM pâtissier.
            </p>
            
            <p className="text-lg text-[#2D2D2D] leading-relaxed mb-4">
              Mais ma véritable école, ça a surtout été le terrain.
            </p>
            
            <p className="text-lg text-[#2D2D2D] leading-relaxed mb-4">
              Après plusieurs expériences en artisanat, j'ai fait une création en 2004 sous l'enseigne Banette, après une formation à l'école Banette. En quelques années, notre boulangerie-pâtisserie de village est passée de 165 K€ à 230 K€ de chiffre d'affaires.
            </p>
            
            <p className="text-lg text-[#2D2D2D] leading-relaxed mb-4">
              En 2008, j'ai dû vendre l'entreprise suite à la maladie grave de ma fille.
            </p>
            
            <p className="text-lg text-[#2D2D2D] leading-relaxed mb-4">
              J'ai ensuite continué à exercer ce métier sous de nombreuses formes : boulanger, pâtissier, chocolatier, confiseur, restauration, traiteur… jusqu'à devenir second de cuisine.
            </p>
            
            <p className="text-lg text-[#2D2D2D] leading-relaxed mb-4">
              J'ai également travaillé huit ans en grande distribution, comme Manager BVP.
            </p>
            
            <p className="text-lg text-[#2D2D2D] leading-relaxed mb-4">
              Au fil des années, j'ai connu les réalités du métier : les charges, les équipes, les horaires, les difficultés de rentabilité, les invendus… mais aussi la passion immense que ce métier peut offrir.
            </p>
            
            <p className="text-lg text-[#2D2D2D] leading-relaxed">
              Aujourd'hui, avec Croissance 🥐consulting, j'aide les artisans boulangers et pâtissiers à améliorer leur rentabilité, leur organisation et leur visibilité, avec une approche concrète, humaine et basée sur la réalité du terrain.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
            Mes valeurs
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-[#FBF7EF] rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
                >
                  <div className="w-14 h-14 bg-[#6B7A3A]/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-[#6B7A3A]" size={28} />
                  </div>
                  <h3 className="text-lg font-semibold text-[#4A3A2A] font-poppins mb-2">
                    {value.title}
                  </h3>
                  <p className="text-[#2D2D2D] text-sm">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="testimonials-section">
        <div className="container mx-auto max-w-5xl">
          <div className="max-w-3xl mx-auto mb-12 space-y-4">
            <p className="text-[#2D2D2D] leading-relaxed text-center">
              Je démarre aujourd'hui cette activité de conseil indépendante après plus de 25 ans sur le terrain, entre artisanat, boulangerie, pâtisserie, restauration et management.
            </p>
            <p className="text-[#2D2D2D] leading-relaxed text-center italic">
              Les premiers accompagnements sont en cours, et d'autres retours arriveront prochainement.
            </p>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
            Ils m'ont fait confiance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-[#B65C3A]">
              <p className="text-[#2D2D2D] leading-relaxed italic mb-6">
                « Christophe a su poser un regard juste sur notre boulangerie. Ses conseils concrets sur la marge, la gamme et l'organisation nous ont permis de retrouver de la clarté et de la sérénité au quotidien. »
              </p>
              <p className="text-[#4A3A2A] font-semibold font-poppins">
                Nathalie F.
              </p>
              <p className="text-sm text-[#6B7A3A]">
                Artisan boulangère
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-[#6B7A3A]">
              <p className="text-[#2D2D2D] leading-relaxed italic mb-6">
                « Une approche humaine et terrain. Christophe comprend immédiatement la réalité du métier. Grâce à son diagnostic, j'ai identifié des leviers simples qui ont vraiment fait la différence sur ma rentabilité. »
              </p>
              <p className="text-[#4A3A2A] font-semibold font-poppins">
                Christelle P.
              </p>
              <p className="text-sm text-[#6B7A3A]">
                Artisan pâtissière
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins mb-8">
            Restons en contact
          </h2>
          
          <div className="bg-[#FBF7EF] rounded-2xl p-8 mb-8">
            <div className="space-y-4 mb-8">
              <a
                href="mailto:christophe.marrama@croissanceconsulting.net"
                className="flex items-center justify-center space-x-3 text-[#2D2D2D] hover:text-[#B65C3A] transition-colors"
              >
                <Mail size={24} />
                <span className="text-lg">christophe.marrama@croissanceconsulting.net</span>
              </a>
              <a
                href="tel:+33674245563"
                className="flex items-center justify-center space-x-3 text-[#2D2D2D] hover:text-[#B65C3A] transition-colors"
              >
                <Phone size={24} />
                <span className="text-lg">06 74 24 55 63</span>
              </a>
            </div>

            <div className="flex justify-center space-x-4 mb-8">
              <a
                href="https://www.threads.com/@christophemarrama?xmt=AQF0LV9B_1fOGLWGucuOURdnx2XDaLb-AU9XBKZItt1r-3E"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-[#B65C3A]/10 hover:bg-[#B65C3A]/20 rounded-full flex items-center justify-center transition-colors"
                aria-label="Instagram Threads"
              >
                <Instagram className="text-[#B65C3A]" size={24} />
              </a>
              <a
                href="https://www.linkedin.com/in/marrama-christophe-91485a311"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-[#B65C3A]/10 hover:bg-[#B65C3A]/20 rounded-full flex items-center justify-center transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="text-[#B65C3A]" size={24} />
              </a>
            </div>
          </div>

          <Link to="/contact">
            <Button className="bg-[#B65C3A] hover:bg-[#9d4e2f] text-white font-medium px-10 py-6 text-lg">
              Proposer un échange
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default About;
